import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Chip,
} from '@mui/material';
import { fetchOrders, updateOrderStatus } from '../store/ordersSlice';
import { RootState } from '../store';
import { useAppDispatch } from '../store/hooks';

const statusColors = {
  pending: 'warning',
  processing: 'info',
  shipped: 'primary',
  delivered: 'success',
} as const;

const Orders: React.FC = () => {
  const dispatch = useAppDispatch();
  const { items: orders, loading } = useSelector((state: RootState) => state.orders);

  useEffect(() => {
    dispatch(fetchOrders());
  }, [dispatch]);

  const handleStatusChange = async (orderId: number, newStatus: string) => {
    await dispatch(updateOrderStatus({ id: orderId, status: newStatus as any }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        Orders
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order ID</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Items</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <TableRow key={order.id}>
                <TableCell>#{order.id}</TableCell>
                <TableCell>
                  {new Date(order.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {order.items.map((item) => (
                    <Box key={item.id} display="flex" alignItems="center" mb={1}>
                      <img
                        src={item.product.image_url}
                        alt={item.product.name}
                        style={{ width: 40, height: 40, objectFit: 'cover', marginRight: 8 }}
                      />
                      <Box>
                        <Typography variant="body2">{item.product.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          Quantity: {item.quantity} × ${item.price.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </TableCell>
                <TableCell>${order.total_amount.toFixed(2)}</TableCell>
                <TableCell>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={order.status}
                      onChange={(e) => handleStatusChange(order.id, e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="processing">Processing</MenuItem>
                      <MenuItem value="shipped">Shipped</MenuItem>
                      <MenuItem value="delivered">Delivered</MenuItem>
                    </Select>
                  </FormControl>
                  <Chip
                    label={order.status}
                    color={statusColors[order.status]}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Orders; 